import VideoJS from 'video.js';
import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs';
import assign from 'lodash/assign';
import kebabCase from 'lodash/kebabCase';

import * as ActionCreators from '../actions/ActionCreators';
import * as ActionTypes from '../actions/ActionTypes';
import * as Controls from './UIControlsNames';
import * as VideoTypes from '../player/videoSource/videoTypes';
import { inIframe, modernizrDetect } from '../utils/ASVPUtils';

import {
    VIDEOS_SITE_URL,
    FORBIDDEN_SLATE,
    NOT_FOUND_SLATE,
    PROBLEM_SLATE,
    RECONNECTING_SLATE,
} from '../constants';


const MIDROLL_CLASSNAME = 'midroll-dot';

// initialization for share control
const shareControlInitEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.INIT)
    .filter(() => Boolean(options.share)) // don't show if options force no share
    .map(() => (player && player.controlBar) ? player.controlBar.getChild(Controls.SHARE_CONTROL) : null)
    .switchMap(shareControl => {
        if (!shareControl) {
            return Observable.empty();
        }
        return Observable.fromEvent(shareControl, 'click')
            .map(() => store.getState().player.share)
            .map(share => share ? ActionCreators.onShareControlClose() : ActionCreators.onShareControlClick());
    });

/* adult swim return home control */
const controlsAdultSwimReturnEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.SET_VIDEO_PLAYER_DATA)
    .filter(() => inIframe())
    .map(() => {
        if (!player || !player.controlBar) {
            return false;
        }
        return player.controlBar.getChild(Controls.WATCH_BUTTON) || player.controlBar.addChild(Controls.WATCH_BUTTON);
    })
    .filter(Boolean)
    .do(control => {
        const { collectionTitle, title } = store.getState().videoSource;
        const url = `${VIDEOS_SITE_URL}${kebabCase(collectionTitle)}/${kebabCase(title)}`;
        control.url(url);
    })
    .ignoreElements();


/* AD countdown display */
const adCountdownEpic = (action$, __, { player, ads }) => action$
    .ofType(ActionTypes.SET_AD_PLAYING_STATE)
    .pluck('payload')
    .do(adPlaying => {
        if (adPlaying) {
            const adCountdown = player.getChild(Controls.AD_COUNTDOWN) || player.addChild(Controls.AD_COUNTDOWN);
            adCountdown.slot(ads.getCurrentSlot());
        } else {
            player.removeChild(Controls.AD_COUNTDOWN);
        }
    })
    .ignoreElements();


/* AD Midroll points on progress bar */
const adMidrollPointsEpic = (action$, store, { player, ads }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_LOADED_DATA)
    .filter(() => !!store.getState().player.adsEnabled) // when ads are turned on
    .filter(() => !store.getState().player.adPlaying) // when an ad is not playing
    .filter(() => !!ads.midrolls.length) // only when there are midrolls
    .do(() => {
        const progressControl = player.controlBar ? player.controlBar.getChild(Controls.PROGRESS_CONTROL) : null;

        if (!progressControl) {
            return;
        }

        for (var i = 0; i < ads.midrolls.length; i++) {
            const slot = ads.midrolls[i];
            const slotTimePosition = slot.getTimePosition();
            const div = document.createElement('div');

            div.className = MIDROLL_CLASSNAME;
            div.style.left = (slotTimePosition / store.getState().videoSource.duration * 100) + '%';
            progressControl.el().appendChild(div);
        }
    })
    .ignoreElements();


const removeAdMidrollPointsEpic = (action$, store, { player }) => Observable.merge(
        action$.ofType(ActionTypes.SET_VIDEO_PLAYER_DATA),
        action$.ofType(ActionTypes.ADS_MIDROLL_END),
    )
    .filter(() => !!store.getState().player.adsEnabled) // when ads are turned on
    .do(() => {
        const progressControl = player.controlBar ? player.controlBar.getChild(Controls.PROGRESS_CONTROL) : null;

        if (progressControl) {
            const previousMidrolls = progressControl.el().getElementsByClassName(MIDROLL_CLASSNAME);
            while (previousMidrolls.length > 0) { // remove previous midrolls from previous content
                previousMidrolls[0].parentNode.removeChild(previousMidrolls[0]);
            }
        }
    })
    .ignoreElements();



// if the live state is newly changed and it's false but it should be a stream, throw up the reconnecting slate
const showReconnectingSlateEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.SET_LIVE_STATE)
    .pluck('payload')
    .distinctUntilChanged()
    .filter(isLive => isLive === false)
    .map(() => store.getState().videoSource.type)
    .filter(videoType => (videoType === VideoTypes.STREAM))
    .do(() => player.poster(RECONNECTING_SLATE)) // set the poster
    .do(() => player.pause()) // pause the content
    .delay(10000) // wait a few seconds
    .switchMapTo(Observable.of(
        ActionCreators.setLiveState(null), // reset live state so it knows to check again for a broken stream
        ActionCreators.reconnectStream() // try to reconnect the stream
    ));


const show403SlateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.HLS_EVENT_LOAD_ERROR)
    .pluck('payload', 'networkDetails', 'status')
    .filter(status => status === 403)
    .do(() => player.pause()) // pause the content
    .do(() => player.poster(FORBIDDEN_SLATE))
    .ignoreElements();


const show404SlateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.HLS_EVENT_LOAD_ERROR)
    .pluck('payload', 'networkDetails', 'status')
    .filter(status => status === 404)
    .do(() => player.pause()) // pause the content
    .do(() => player.poster(NOT_FOUND_SLATE))
    .ignoreElements();


const showProblemSlateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.HLS_EVENT_LOAD_ERROR)
    .pluck('payload', 'networkDetails', 'status')
    .filter(status => status !== 404 && status !== 403)
    .do(() => player.pause()) // pause the content
    .do(() => player.poster(PROBLEM_SLATE))
    .ignoreElements();


const showShareOverlayEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.ON_SHARE_CONTROL_CLICK)
    .filter(() => store.getState().player.share)
    .switchMap(() => {
        const { player: playerState, videoSource } = store.getState();

        player.addClass('as-sharing');

        const ShareOverlay = VideoJS.getComponent(Controls.SHARE_OVERLAY);

        const content = new ShareOverlay(player, assign({
            video: videoSource
        }, options));

        const modal = player.createModal(content.el(), {
            label: 'Share',
            pauseOnOpen: playerState.adPlaying || !store.getState().player.isLive
        });

        modal.addClass('as-share-modal-dialog');

        const modalClose$ = Observable.fromEvent(modal, 'modalclose')
            .do(() => {
                content.dispose();
                player.removeClass('as-sharing');
            })
            .take(1)
            .filter(() => store.getState().player.share)
            .mapTo(ActionCreators.onShareControlClose());

        const close$ = action$
            .ofType(ActionTypes.ON_SHARE_CONTROL_CLOSE, ActionTypes.SET_VIDEO_PLAYER_DATA)
            .filter(() => modal && modal.opened())
            .do(() => modal.close())
            .ignoreElements();

        const userInteraction$ = Observable.fromEvent(content, 'userInteraction', (event, data) => data)
            .takeUntil(Observable.fromEvent(content, 'dispose'))
            .map(ActionCreators.trackUserInteraction);

        return Observable.merge(
            close$,
            modalClose$,
            userInteraction$
        );
    });

const mutedOverlayEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.INIT)
    .take(1)
    .switchMap(() => Observable.zip(modernizrDetect('videoautoplay'), modernizrDetect('touchevents')))
    .filter(([videoautoplay, touchevents]) => !videoautoplay && !touchevents && player.playsinline())
    .do(() => player.muted(true))
    .switchMap(() => Observable.fromPromise(player.play()).mapTo(true).catch(() => Observable.of(false)))
    .filter(Boolean)
    .do(() => player.addChild(Controls.MUTED_OVERLAY_BUTTON, undefined, 1))
    .ignoreElements();


export default combineEpics(
    controlsAdultSwimReturnEpic,
    adCountdownEpic,
    adMidrollPointsEpic,
    removeAdMidrollPointsEpic,
    showReconnectingSlateEpic,
    show403SlateEpic,
    show404SlateEpic,
    showProblemSlateEpic,
    shareControlInitEpic,
    showShareOverlayEpic,
    mutedOverlayEpic
);




// WEBPACK FOOTER //
// ./src/ui/epics.js